import { useMemo, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Link } from 'Components/Buttons/Buttons';
import { AssociatedDocumentsModal } from 'Components/Modal/AssociatedDocumentsModal/AssociatedDocumentsModal';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table, TableProps } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { getCreateFolderUrl, getFolderDetailsUrl } from 'Helpers/URLBuilder/URLBuilder';
import { UploadedFile } from 'Models/Files';
import { FolderVersion, ManageFolderOrigin } from 'Models/Folders';
import { OrganizationBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ColorTheme } from 'Models/Types/GlobalType';

import styles from './FoldersListing.module.css';

interface FolderRow extends FolderVersion {
    organizationName: string;
}

export interface FoldersTableProps {
    createButtonVariant: 'primaryButton' | 'secondaryButton';
    createFolderOrigin: ManageFolderOrigin; // Supplies the context for an origin-aware breadcrumb on the folder pages reached from this listing.
    documentApi: DocumentApi;
    folders: FolderVersion[];
    folderTypes: string[];
    module: DueDiligenceModule;
    organizations: OrganizationBase[];

    /** The type of background that the component will be displayed against. If not supplied, `'light'` will be used. */
    colorTheme?: ColorTheme;
    /** If `true`, the organization ("Third Party" or "Business") column and filter are omitted. Used on the TPRM Service Dashboard and the UCRM Use Case Dashboard, where the listing must be scoped to a single organization. */
    hideOrganizationInfo?: boolean;
    /** If `true`, a "Folders" title and an adjacent "Create Folder" button are displayed. Used on the Service Dashboard. */
    showTitleAndCreateButton?: boolean;
    preselectedOrganizationIdForCreate?: string;
    preselectedOrganizationIdForFilter?: string;
}

/**
 * Renders a table of current folders.
 * Clicking a folder name goes to the Manage Folder page. Both that link and the "Create Folder" button encode `createFolderOrigin`, so that the destination page's breadcrumb leads back to wherever this listing is rendered.
 */
export const FoldersTable = (props: FoldersTableProps) => {
    const [displayedDocuments, setDisplayedDocuments] = useState<UploadedFile[]>();

    const rows: FolderRow[] = useMemo(() => {
        const organizationIdToName: Map<string, string> = new Map(props.organizations.map((organization) => [organization.id, organization.name]));
        return props.folders.map((folder) => ({ ...folder, organizationName: organizationIdToName.get(folder.third_party_id)! }));
    }, [props.folders, props.organizations]);

    const organizationFilterOptions = useMemo(() => props.organizations.map((organization) => ({ name: organization.name, value: organization.id })).sort((a, b) => a.name.localeCompare(b.name)), [props.organizations]);

    const columns: ColumnConfig<FolderRow>[] = [
        {
            type: ColumnType.STRING,
            name: 'Folder Name',
            getString: (folder) => folder.name,
            getOnClickTextAction: (folder) => ({ goToLocation: getFolderDetailsUrl(folder.third_party_id, folder.id, props.createFolderOrigin) }),
        },
        {
            type: ColumnType.STRING,
            name: 'Folder Type',
            getString: (folder) => folder.type,
        },
        {
            type: ColumnType.MULTIPLE_FILES,
            name: 'Documents',
            getFiles: (folder) => folder.files ?? [],
            onClick: (files) => setDisplayedDocuments(files),
        },
        ...(props.hideOrganizationInfo
            ? []
            : [
                  {
                      type: ColumnType.STRING as const,
                      name: props.module === ProductModule.UCRM ? 'Business' : 'Third Party',
                      getString: (folder: FolderRow) => folder.organizationName,
                  },
              ]),
        {
            type: ColumnType.DATE,
            name: 'Effective Date',
            getDateString: (folder) => folder.effective_date,
        },
        {
            type: ColumnType.DATE,
            name: 'Expiration Date',
            getDateString: (folder) => folder.expiration_date,
        },
    ];

    const filters: FilterConfig<FolderRow>[] = [
        ...(props.hideOrganizationInfo
            ? []
            : [
                  {
                      type: FilterType.SINGLE_PROPERTY as const,
                      id: 'organizationFilter',
                      label: props.module === ProductModule.UCRM ? 'Filter by Business' : 'Filter by Third Party',
                      defaultSelections: props.preselectedOrganizationIdForFilter ? [props.preselectedOrganizationIdForFilter] : undefined,
                      getFilterableDataForRow: (folder: FolderRow) => [folder.third_party_id],
                      options: organizationFilterOptions,
                      canSelectMultiple: true,
                      width: 'large' as const,
                  },
              ]),
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'folderTypeFilter',
            label: 'Filter by Folder Type',
            getFilterableDataForRow: (folder) => [folder.type],
            options: props.folderTypes.map((type) => ({ name: type, value: type })),
            canSelectMultiple: true,
            width: 'large',
        },
    ];

    const tableProps: TableProps<FolderRow> = {
        columns,
        filters,
        defaultSortColumn: 'Folder Name',
        rowsData: rows,
        getUniqueIdForRow: (folder) => folder.id,
        pageVariant: (props.colorTheme ?? 'light') === 'light' ? 'standard' : 'dashboard',
    };

    const createButtonDestination = getCreateFolderUrl(props.createFolderOrigin, props.preselectedOrganizationIdForCreate);

    return (
        <>
            {displayedDocuments && <AssociatedDocumentsModal associatedDocuments={displayedDocuments} documentApi={props.documentApi} hideModal={() => setDisplayedDocuments(undefined)} />}
            {props.showTitleAndCreateButton && (
                <div className={styles.titleAndButtonContainer}>
                    <Text variant="Header2" color={props.colorTheme === 'dark' ? 'white' : 'blue'}>
                        Folders
                    </Text>
                    <Link variant={props.createButtonVariant} to={createButtonDestination} fontAwesomeImage={ICON_ADD_CREATE}>
                        Create Folder
                    </Link>
                </div>
            )}
            <Table {...tableProps} />
        </>
    );
};
